import * as React from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import { useMachine } from "@xstate/react";
import { useOffer, useOfferMode } from "@nju/data-access-portal-offers";
import { useOffers } from "@nju/data-access-portal-offers";
import { useOrchestrators } from "@nju/data-access-portal-orchestrator";
import { pushSimpleEvent } from "@nju/data-layer";
import { FAQButton } from "@nju/feature-portal-help";
import { SidebarOffer } from "@nju/feature-portal-sidebar";
import { CancelOrderModal } from "@nju/feature-sales-order";
import { ErrorMessage } from "@nju/form";
import { portalRoutes, salesRoutes } from "@nju/routes";
import { IScope } from "@nju/scope";
import {
  A,
  Alert,
  BlockContent,
  Button,
  ButtonsWrapper,
  GeneralErrorNotification,
  getAlertEsimText,
  Heading,
  InfoPanel,
  Layout,
  Main,
  Modal,
  Separator,
  Side,
  useOverlay,
} from "@nju/ui";
import { OnboardingMembersInfo } from "../OnboardingMembersInfo";
import {
  INumberDecisionType,
  SelectNewOrExistingNumberForm,
  selectNewOrExistingNumberMachine,
} from "./partials";

interface ISelectNewOrExistingNumberPageProps {
  variant?: IScope;
}

export function SelectNewOrExistingNumberPage({
  variant = "portal",
}: ISelectNewOrExistingNumberPageProps) {
  const { mobile } = useOffers();
  const resolvedOffer = mobile.indefiniteOffers.find((offer) => {
    return offer.display;
  });
  const price =
    resolvedOffer?.ftthBundleDiscount || resolvedOffer?.bundleDiscount;
  const router = useRouter();
  const { multiOrder } = useOrchestrators();
  const params = portalRoutes.czyZachowacNumer.input(router.query).unwrap();
  const offerId = params.oferta ?? multiOrder?.offerId;
  const offer = useOffer(offerId);
  const cancelOrderModal = useOverlay();
  const mode = useOfferMode();
  const isFormRegistration = portalRoutes.czyZachowacNumer.hasParam(
    router.query,
    "type",
  );

  const [{ context, matches }, send] = useMachine(
    selectNewOrExistingNumberMachine,
    {
      context: {
        variant,
      },
      actions: {
        redirectToChooseNewNumberPage: () => {
          if (offer?.offerId) {
            if (variant !== "sales") {
              router.push(
                portalRoutes.wybierzNumer.get({
                  mode,
                  oferta: offer.offerId,
                  type: "registration",
                }),
              );
            }

            if (variant === "sales") {
              router.push(
                salesRoutes.wybierzNumer.get({
                  mode: "mobile",
                  oferta: offer.offerId,
                }),
              );
            }
          }
        },
        redirectToMigrateNumberPage: () => {
          if (offer?.offerId) {
            if (variant !== "sales") {
              router.push(
                portalRoutes.migracjaNumeru.index.get({
                  mode: "mobile",
                  oferta: offer.offerId,
                  type: "registration",
                }),
              );
            }

            if (variant === "sales") {
              router.push(
                salesRoutes.migracjaNumeru.index.get({
                  mode: "mobile",
                  oferta: offer.offerId,
                }),
              );
            }
          }
        },
      },
    },
  );

  const { errorMessage } = context;

  const handleNumberDecisionMethodChange = React.useCallback(
    (value: INumberDecisionType) => {
      send({ type: "SET_NUMBER_DECISION", value });
    },
    [send],
  );

  const handleNextStep = React.useCallback(() => {
    send({ type: "SUBMIT" });
  }, [send]);

  const handlePushDataLayer = React.useCallback(() => {
    pushSimpleEvent({
      event: "activity_service",
      eventName: "process_finish_later",
    });
  }, []);

  const isLoadingNextStep =
    variant !== "sales"
      ? matches("goToNextStep")
      : matches("createOrchestrator");

  if (matches("error")) {
    return (
      <GeneralErrorNotification
        callback={() => {
          return router.push(portalRoutes.mojeKonto.index.get({ mode }));
        }}
      />
    );
  }

  return (
    <>
      {price && variant === "sales" && (
        <Alert iconName="info-circle" text={getAlertEsimText(price)} />
      )}
      <BlockContent>
        {isFormRegistration && (
          <>
            <Separator size="m" tone="invisible" />
            <InfoPanel
              description="Z tego samego loginu i hasła możesz też korzystać w aplikacji mobilnej nju."
              icon="check-circle"
              title="konto założone!"
            />
          </>
        )}
        {variant !== "sales" && (
          <OnboardingMembersInfo isMulti={false} multiOrder={multiOrder} />
        )}
        <Layout>
          <Main>
            <Heading
              className="sm:mb-3 mb-4"
              font="ttfirs"
              tone="neutral"
              variant="h2"
              weight="bold"
            >
              {variant !== "sales" && "co chcesz zrobić?"}
              {variant === "sales" && "co chce zrobić klient?"}
            </Heading>
            <Separator size="s" tone="invisible" />
            <SelectNewOrExistingNumberForm
              handleNumberDecisionMethodChange={
                handleNumberDecisionMethodChange
              }
              variant={variant}
            />
            <FAQButton categoryId="NJU_WWW_PROCESS_SELECTION" />
            {errorMessage && <ErrorMessage message={errorMessage} />}
            <ButtonsWrapper>
              {variant !== "sales" && (
                <Link
                  href={portalRoutes.mojeKonto.index.get({
                    mode,
                    flow: "multiOrder",
                  })}
                  passHref={true}
                >
                  <A
                    onClick={handlePushDataLayer}
                    size={{
                      "@onlyMobile": "s",
                    }}
                    variant="secondary"
                  >
                    dokończ później
                  </A>
                </Link>
              )}
              {variant === "sales" && (
                <Button
                  display="inline"
                  onPress={cancelOrderModal.open}
                  size={{
                    "@onlyMobile": "s",
                  }}
                  variant="secondary"
                >
                  anuluj
                </Button>
              )}
              <Button
                isLoading={isLoadingNextStep}
                onPress={handleNextStep}
                size={{
                  "@onlyMobile": "s",
                }}
                type="submit"
              >
                dalej
              </Button>
            </ButtonsWrapper>
          </Main>
          <Side>
            <SidebarOffer
              offer={offer}
              orchestrator={multiOrder}
              variant={variant}
            />
          </Side>
          {multiOrder && cancelOrderModal.isOpened && (
            <Modal
              onClose={cancelOrderModal.close}
              title="anulowanie zamówienia"
            >
              <CancelOrderModal
                mode={mode}
                onClose={cancelOrderModal.close}
                orchestratorId={multiOrder.id}
              />
            </Modal>
          )}
        </Layout>
      </BlockContent>
    </>
  );
}
